import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import Scrollbar from 'react-scrollbars-custom';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import CircularProgress from '@material-ui/core/CircularProgress';
import { getCustomCategories, getCustomCategoriesValues } from '../../store/customCategories/actions';
import { customCategoriesSelector, customCategoriesValuesSelector } from '../../store/customCategories/selectors';

import { loadEmployeesAll, loadLogbookJournal } from '../../store/settings/actions';
import { JournalDataSelector } from '../../store/settings/selectors';
import StyledCheckbox from '../Core/Checkbox/Checkbox';
import MainLayout from '../Core/MainLayout';
import LabelWithCurrencySignComa from '../shared/LabelWithCurrencySignComa';
import styles from './Reports.module.scss';
import DRP from '../Core/DRP/DRP';
import Button from '../Core/Button/Button';
import DataTable from '../Core/DataTableCustom/DTM';
import {
  reportSelector,
  columnsSelector,
  reportLoadingSelector,
} from '../../store/reports/selectors';
import {
  employeesSelector,
} from '../../store/settings/selectors';
import { jobTypesSelector } from '../../store/jobTypes/selectors';
import { getJobTypes } from '../../store/jobTypes/actions';
import { getPlaces } from '../../store/places/actions';
import { placesSelector } from '../../store/places/selectors';

import InfoIcon from '../Icons/InfoIcon';
import ClosableCard from '../Core/ClosableCard/ClosableCard';
import ReportsIcon from '../Icons/ReportsIcon';
import CheckboxGroupV2 from '../Core/CustomSelect/CheckboxGroup'
import ArrowRightIcon from '../Icons/ArrowRightIcon';
import {
  getReport, downloadExcel, downloadPdf, downloadRupExcel, downloadMiliusExcel,
} from '../../store/reports/actions';
import { dateToUCT } from '../Helpers';
import { skillsSelector } from '../../store/skills/selectors';
import { getSkills } from '../../store/skills/actions';
import usePermissions from '../Core/usePermissions';
import useCompanyInfo from '../../hooks/useCompanyInfo';

import FavoriteFilters from '../FavotiteFilters'
import { generateResourcesFromEmployees, getCheckedEmployeeIds, setCheckedEmployees as setCheckedStatus, updateAllEmployees } from '../../components/TimeOff/Calendar/utils'

const profitabilityColumns = [
  { label: <LabelWithCurrencySignComa label='Cost' />, field: 'cost', checked: true },
  { label: <LabelWithCurrencySignComa label='Earnings' />, field: 'sallary', checked: true },
  { label: <LabelWithCurrencySignComa label='Profit' />, field: 'profit', checked: true },
];

const permissionsConfig = [
  {
    name: 'reports_generate',
    permission: 'reports_generate',
  },
  {
    name: 'reports_generate',
    permission: 'reports_generate',
  },
  {
    name: 'cost',
    module: 'cost_earning',
    permission: 'reports_costs',
  },
  {
    name: 'profit',
    module: 'profitability',
    permission: 'reports_profit',
  },
  {
    name: 'earnings',
    module: 'profitability',
    permission: 'reports_earnings',
  },
  {
    name: 'places',
    module: 'create_places',
  },
  {
    name: 'jobs',
    module: 'create_jobs',
  },
  {
    name: 'reports_assigned_place',
    permission: 'reports_assigned_place',
    default: false,
  },
  {
    name: 'night_rates',
    module: 'night_rates',
  },
  {
    name: 'create_groups',
    module: 'create_groups',
  },
  {
    name: 'custom_category',
    module: 'custom_category',
  },
  {
    name: 'comments_photo',
    module: 'comments_photo',
  }
];

const useStyles = makeStyles({
  colorPrimary: {
    color: '#0087ff',
  },
});

export default () => {
  const { getDateFormat } = useCompanyInfo();

  const classes = useStyles();

  /* Reports data */
  const reportTabs = useRef(null);
  const [activeReport, setActiveReport] = useState();
  const [generateRequest, setGenerateRequest] = useState();

  /* Data table */
  const [itemsArray, setItemsArray] = useState([]);
  const [columnsArray, setColumnsArray] = useState([]);

  const allCustomCategories = useSelector(customCategoriesSelector);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(null);

  const [dateRange, setDateRange] = useState({ startDate: startOfMonth(new Date()), endDate: endOfMonth(new Date()) });

  const [jobTypes, setJobTypes] = useState([]);

  const [employees, setEmployees] = useState([]);

  const [places, setPlaces] = useState([]);

  const [skills, setSkills] = useState([]);

  const [filteredCustomCategory, setFilteredCustomCategory] = useState({});

  const [totalStat, setTotalStat] = useState({
    cost: 0,
    sallary: 0,
    profit: 0,
  });

  const [costState, setCostState] = useState({
    show_costs: false,
    show_earnings: false,
    show_profit: false,
  });

  const [showImages, setShowImages] = useState(false)

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id: companyId } = useParams();
  const permissions = usePermissions(permissionsConfig);

  const generatedReport = useSelector(reportSelector);
  const reportLoading = useSelector(reportLoadingSelector);
  const columns = useSelector(columnsSelector);

  const { users: getAllEmployees } = useSelector(employeesSelector);
  const getAllJobTypes = useSelector(jobTypesSelector);
  const getAllPlaces = useSelector(placesSelector);
  const getAllSkills = useSelector(skillsSelector);
  const getAllCustomCategoriesValues = useSelector(customCategoriesValuesSelector);
  const { end_day_comment: comments = false, end_day_photo } = useSelector(JournalDataSelector);

  const mainContainerClasses = classNames(styles.mainContainer, {
    [styles.mainContainerWithReports]: itemsArray.length,
  });

  useEffect(() => {
    dispatch(getJobTypes(companyId));
    dispatch(getPlaces(companyId));
    dispatch(loadEmployeesAll(companyId, { page: 'reports' }));
    dispatch(getSkills(companyId));
    dispatch(loadLogbookJournal(companyId));
    dispatch(getCustomCategories(companyId));
    dispatch(getCustomCategoriesValues(companyId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const costsCheckboxesHandler = (id, checked) => {
    setCostState((prevState) => ({
      ...prevState,
      ...(id !== 'show_costs' && checked
        ? { show_costs: true, [id]: checked }
        : { [id]: checked }
      ),
    }));
  };

  const showCostsInReport = () => {
    // eslint-disable-next-line no-underscore-dangle
    const _temp = {};
    Object.keys(costState).map((key) => {
      if (costState[key]) {
        _temp[key] = 1;
      }
      return key;
    });
    return _temp;
  };

  const sendRequest = () => {
    setGenerateRequest(true);
    const { startDate, endDate } = dateRange;
    const placesArr = places.filter(p => p.checked).map((place) => place.id);
    const jobTypesArr = jobTypes.filter(jt => jt.checked).map((spec) => spec.id);
    const employeesArr = getCheckedEmployeeIds(employees)
    const skillsArr = skills.filter(s => s.checked).map((emp) => emp.id);
    const customCategoryValuesArr = Object.values(filteredCustomCategory).flat().filter(item => item.checked).map(item => item.id)
    
    dispatch(getReport(companyId, {
      startDate: startDate ? format(startDate, 'yyyy-MM-dd HH:mm:ss') : undefined,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd HH:mm:ss') : undefined,
      jobTypesArr,
      employeesArr,
      placesArr,
      skillsArr,
      customCategoryValuesArr,
      ...showCostsInReport(),
      show_images: showImages*1,
    })).then().catch();
  };

  useEffect(() => {
    if (Array.isArray(getAllPlaces)) {
      setPlaces(getAllPlaces.map((p) => ({ ...p, title: p.name, checked: false, isEmployee: true })))
    }
  }, [getAllPlaces]);

  useEffect(() => {
    if (Array.isArray(getAllEmployees)) {
      setEmployees(generateResourcesFromEmployees(getAllEmployees))
    }
  }, [getAllEmployees]);

  useEffect(() => {
    if (Array.isArray(getAllJobTypes)) {
      setJobTypes(getAllJobTypes.map((jt) => ({ ...jt, title: jt.title, checked: false, isEmployee: true })))
    }
  }, [getAllJobTypes]);

  useEffect(() => {
    if (Array.isArray(getAllSkills)) {
      setSkills(getAllSkills.map(s => ({...s, checked: false, isEmployee: true, title: s.label})));
    }
  }, [getAllSkills]);

  useEffect(() => {
    if (Array.isArray(getAllCustomCategoriesValues)) {
      const temp = getAllCustomCategoriesValues.reduce((acc, item) => {
        return {
          ...acc,
          [item.custom_category_id]: (acc[item.custom_category_id] || []).concat({...item, checked: false, isEmployee: true, title: item.value})
        }
      }, {})
      setFilteredCustomCategory(temp)
    }
  }, [getAllCustomCategoriesValues])

  useEffect(() => {
    const formatDate = getDateFormat({
      'YY.MM.DD': 'yyyy-MM-DD',
      'DD.MM.YY': 'DD-MM-yyyy',
      'MM.DD.YY': 'MM-DD-yyyy',
    });
    
    if (generatedReport.report && (generateRequest || activeReport)) {
      setItemsArray((state) => {
        const reportId = generatedReport.id;
        const reportIndex = state.findIndex(({ id }) => id === reportId);

        let reportsCost = 0;
        let reportsSallary = 0;
        let reportsProfit = 0;
        
        const mappedReport = {
          ...generatedReport,
          dateRange,
          placesArr: places.filter(p => p.checked).map((place) => place.id),
          jobTypesArr: jobTypes.filter(jt => jt.checked).map((spec) => spec.id),
          employeesArr: getCheckedEmployeeIds(employees),
          skillsArr: skills.filter(s => s.checked).map((emp) => emp.id),
          customCategoryValuesArr: Object.values(filteredCustomCategory).flat().filter(item => item.checked).map(item => item.id),
          description: `${moment(generatedReport.startDate, 'YYYY-MM-DD HH:mm:ss').format(formatDate)} - ${moment(generatedReport.endDate, 'YYYY-MM-DD HH:mm:ss').format(formatDate)}`,
          report: generatedReport.report.map(({ items, ...rest }) => ({
            ...rest,
            items: items.map(({ data, ...other }) => ({
              ...other,
              data: {
                ...data,
                columns: [
                  ...generatedReport.employee_columns.slice(0, 4),
                  {},
                  {},
                  ...generatedReport.employee_columns.slice(4),
                  ...profitabilityColumns,
                ]
                  .filter(({ field }) => {
                    if (!costState.show_earnings && field === 'sallary') {
                      return false;
                    }
                    if (!costState.show_costs && field === 'cost') {
                      return false;
                    }
                    if (!comments && field === 'comment') {
                      return false;
                    }
                    return !(!costState.show_profit && field === 'profit');
                  }),
                items: data.items.map(({ profitability: prof, ...all }) => {
                  const { cost = 0, sallary = 0, profit = 0 } = prof ?? {};
                  reportsCost += cost;
                  reportsSallary += sallary;
                  reportsProfit += profit;
                  return {
                    ...all,
                    cost,
                    sallary,
                    profit,
                  };
                }),
              },
            }))
          })),
        };

        if (reportIndex >= 0) {
          return [
            ...state.slice(0, reportIndex),
            {
              ...mappedReport,
            },
            ...state.slice(reportIndex + 1),
          ];
        }

        setTotalStat((prevState) => ({
          sallary: prevState.sallary + reportsSallary,
          profit: prevState.profit + reportsProfit,
          cost: prevState.profit + reportsCost,
        }));

        return [...state, mappedReport];
      });

      // eslint-disable-next-line
      if (activeReport && !generateRequest && generatedReport.id != activeReport) {
        const removeReport = (reps) => reps.filter((rep) => { return rep.id !== activeReport});
        setItemsArray(removeReport);
      }
      setActiveReport(generatedReport.id);
    }
    setGenerateRequest(null);
    // setTotalDuration(getTotalDuration);
    // eslint-disable-next-line
  }, [comments, costState.show_costs, costState.show_earnings, costState.show_profit, generatedReport]);

  useEffect(() => {
    setColumnsArray([...columns, ...profitabilityColumns].filter(({ field }) => {
      if (!costState.show_earnings && field === 'sallary') return false;
      if (!costState.show_profit && field === 'profit') return false;
      if (!permissions.jobs && field === 'jobType') return false;
      if (!permissions.create_groups && (field === 'group' || field === 'subgroup')) return false;
      if (!permissions.night_rates && field === 'night_duration') return false;
      return !(!costState.show_costs && field === 'cost');
    }));
  }, [activeReport, columns, costState.show_costs,
    costState.show_earnings, costState.show_profit, permissions]);

  useEffect(() => {
    setLoading(reportLoading);
  }, [reportLoading]);

  const sortHandler = useCallback((field, asc) => {
    const sortNumFunction = (a, b) => (asc ? (a[field] - b[field]) : (b[field] - a[field]));
    const sortFunction = (a, b) => {
      if (typeof a[field] === 'number' && typeof b[field] === 'number') {
        return sortNumFunction(a, b);
      }
      if (asc) {
        return a[field].toString().localeCompare(b[field]);
      }
      return b[field].toString().localeCompare(a[field]);
    };
    const sortItems = (array) => {
      const arrayCopy = [...array];
      return arrayCopy.map((report) => {
        if (report.id === activeReport) {
          const newReport = { ...report };
          const tableData = newReport.report.map((group) => {
            const items = [...group.items];
            items.sort(sortFunction);
            return { ...group, items };
          });
          return { ...newReport, data: tableData };
        }
        return report;
      });
    };

    setItemsArray(sortItems);
  }, [activeReport]);

  const Delimiter = () => (<div className={styles.delimiter} />);

  const rowSelectionHandler = (selectedRow) => {
    setSelectedItem(selectedRow);
  };

  const closeReportTabHandler = (e, reportId) => {
    e.stopPropagation();
    const removeReport = (reps) => reps.filter((rep) => rep.id !== reportId);
    setItemsArray(removeReport);
    if (reportId === activeReport) setActiveReport(null);
  };

  const downloadReport = (action, ext) => {
    setLoading(true);

    const selectedReport = itemsArray.find((report) => report.id === activeReport);

    const { startDate, endDate } = selectedReport?.dateRange ? selectedReport?.dateRange : dateRange;
    const placesArr = selectedReport?.placesArr ? selectedReport?.placesArr : places.filter(p => p.checked).map((place) => place.id);
    const jobTypesArr = selectedReport?.jobTypesArr ? selectedReport?.jobTypesArr : jobTypes.filter(jt => jt.checked).map((spec) => spec.id);
    const employeesArr = selectedReport?.employeesArr ? selectedReport?.employeesArr : getCheckedEmployeeIds(employees);
    const skillsArr = selectedReport?.skillsArr ? selectedReport?.skillsArr : skills.filter(s => s.checked).map((emp) => emp.id);
    const customCategoryValuesArr = selectedReport?.customCategoryValuesArr ? selectedReport?.customCategoryValuesArr : Object.values(filteredCustomCategory).flat().filter(item => item.checked).map(item => item.id)

    if (selectedReport) {
      let filter = '';
      if (selectedReport.places?.length && !selectedReport.jobTypes?.length && !selectedReport.employees?.length) {
        filter = 0;
      } else if (!selectedReport.jobTypes?.length && selectedReport.employees?.length) {
        filter = 1;
      } else if (selectedReport.jobTypes?.length && !selectedReport.employees?.length) {
        filter = 2;
      } else if (selectedReport.jobTypes?.length && selectedReport.employees?.length) {
        filter = 3;
      }

      const requestObj = {
        startDate: startDate ? format(startDate, 'yyyy-MM-dd HH:mm:ss') : undefined,
        endDate: endDate ? format(endDate, 'yyyy-MM-dd HH:mm:ss') : undefined,
        jobTypesArr,
        employeesArr,
        placesArr,
        skillsArr,
        customCategoryValuesArr,
        filter,
        ...showCostsInReport(),
        show_images: showImages*1,
      };

      dispatch(action(companyId, requestObj,'reports')).then(({ data }) => {
        const link = document.createElement('a');
        link.setAttribute('download',
          `Report_${format(dateToUCT(selectedReport.startDate),
            'yyyy-MM-dd')}_${format(dateToUCT(selectedReport.endDate),
            'yyyy-MM-dd')}.${ext}`);
        link.href = `data:application/octet-stream;base64,${data}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setLoading(false);
      }).catch();
    }
  };

  const filterChecked = (checkedArray, type, id) => {
    // let requestObj = {};
    switch (type) {
      case 'places':
        setPlaces(checkedArray);
        break;
      case 'employees':
        setEmployees(checkedArray);
        break;
      case 'jobTypes':
        setJobTypes(checkedArray);
        break;
      case 'skills':
        setSkills(checkedArray);
        break;
      default:
        return;
    }
  };

  const handleChangeActiveFavoriteFilter = (data) => {
    const selectedCategories = Object.entries(data).reduce((acc, [key, value]) => {
      const match = key.match(/^custom_category_(\d+)$/)
      if (match && match[1] && Array.isArray(value)) {
        const checkedOptions = value.reduce((acc, categoryOptionId) => {
          return {
            ...acc,
            [categoryOptionId]: true,
          }
        }, [])
        acc[match[1]] = checkedOptions
      }
      return acc
    }, {})
    const updated = Object.entries(filteredCustomCategory).reduce((acc, [categoryId, options]) => {
      return {
        ...acc,
        [categoryId]: options.map(option => {
          return {
            ...option,
            checked: selectedCategories?.[categoryId]?.[option.id] || false
          }
        })
      }
    }, filteredCustomCategory)
    setFilteredCustomCategory(updated)

    const updatedEmployees = data.employees ? setCheckedStatus(employees, data.employees || [], true) : updateAllEmployees(employees, false)
    setPlaces(places.map(p => ({...p, checked: data.places?.includes(p.id)})))
    setSkills(skills.map(s => ({...s, checked: data.skills?.includes(s.id)})))
    setJobTypes(jobTypes.map(jt => ({...jt, checked: data.jobTypes?.includes(jt.id)})))
    setEmployees(updatedEmployees)
    setDateRange({
      startDate: data.start ? moment(data.start, 'DD-MM-YYYY').toDate() : moment().startOf('month').toDate(),
      endDate: data.end ? moment(data.end, 'DD-MM-YYYY').toDate() : moment().endOf('month').toDate(),
    })
    setCostState({
      show_costs: data.show_costs || false,
      show_earnings: data.show_earnings || false,
      show_profit: data.show_profit || false,
    })
    setShowImages(data.show_images || false)
  }

  const handleChangeCustomeCategoryFilter = (categoryId) => (items) => {
    setFilteredCustomCategory(prev => {
      return {
        ...prev,
        [categoryId]: items
      }
    })
  }

  return (
    <MainLayout>
      <div className={styles.container}>
        <div className={styles.leftContent}>
          {itemsArray.length > 0 && (
            <Scrollbar
              className={styles.scrollableContent}
              style={{ height: '85px' }}
              removeTracksWhenNotUsed
              disableTrackYMousewheelScrolling
              noScrollY
              trackXProps={{
                renderer: ({ elementRef, ...restProps }) => (
                  <span
                    {...restProps}
                    ref={elementRef}
                    className={classNames(styles.scrollbarTrackX, { trackX: true })}
                  />
                ),
              }}
              trackYProps={{
                renderer: ({ elementRef, ...restProps }) => (
                  <span
                    {...restProps}
                    ref={elementRef}
                    className={classNames(styles.scrollbarTrackY, { trackY: true })}
                  />
                ),
              }}
            >
              <div
                ref={reportTabs}
                style={{
                  display: 'flex',
                  maxHeight: '85px',
                  paddingRight: '5px',
                  width: 'fit-content',
                  paddingLeft: '10px',
                }}
              >
                {
                  itemsArray.length > 0 && itemsArray.map((report) => (
                    <ClosableCard
                      key={report.id.toString()}
                      title={report.title}
                      description={report.description}
                      reportId={report.id}
                      selected={report.id === activeReport}
                      onClick={setActiveReport}
                      onClose={closeReportTabHandler}
                    />
                  ))
                }
              </div>
            </Scrollbar>
          )}
          <div
            className={mainContainerClasses}
            style={{ height: itemsArray.length > 0 ? 'calc(100vh - 210px)' : 'calc(100vh - 125px)' }}
          >
            {
              itemsArray.length > 0 && activeReport
                ? itemsArray.map((report) => report.id === activeReport && (
                  report.report.length > 0 ? (
                     <DataTable
                       key={report.id.toString()}
                       data={report.report || []}
                       columns={columnsArray || []}
                       columnsWidth={{}}
                       onColumnsChange={setColumnsArray}
                       loading={loading}
                       onSort={sortHandler}
                       selectedItem={selectedItem}
                       total={report.total}
                       setSelectedItem={rowSelectionHandler}
                       reports
                       downloadExcel={() => downloadReport(downloadExcel, 'xlsx')}
                       downloadMiliusExcel={companyId*1 === 150 ? () => downloadReport(downloadMiliusExcel, 'xlsx') : null}
                       downloadRupExcel={companyId*1 === 122 ? () => downloadReport(downloadRupExcel, 'xlsx') : null}
                       downloadPdf={() => downloadReport(downloadPdf, 'pdf')}
                       verticalOffset='212px'
                       amount={totalStat}
                       withCost={permissions.cost && costState.show_costs}
                       withProfit={permissions.cost && permissions.profit && costState.show_profit}
                       withSallary={permissions.cost && permissions.profit && costState.show_earnings}
                       white
                     />
                   ) : <div className={styles.emptyContainer} key={report.id.toString()}>
                    <div className={styles.emptyContent}>
                      <div>
                        <ReportsIcon className={styles.reportsIcon} />
                        <p className={styles.title}>{t('NOTHING FOUND')}</p>
                        <p className={styles.description}>{t('Please select an object or place to generate report')}</p>
                      </div>
                      <div className={styles.arrowIcon}>
                        <ArrowRightIcon />
                      </div>
                    </div>
                  </div>
                ))
                : 
                <div className={styles.emptyContainer}>
                  {
                    loading ? 
                    (
                      <div className={classNames(styles.overlay, { [styles.overlayActive]: loading })}>
                        <CircularProgress classes={{ colorPrimary: classes.colorPrimary }} />
                      </div>
                    ) 
                    : (
                      
                        <div className={styles.emptyContent}>
                          <div>
                            <ReportsIcon className={styles.reportsIcon} />
                            <p className={styles.title}>{t('NOTHING SELECTED')}</p>
                            <p className={styles.description}>{t('Please select an object or place to generate report')}</p>
                          </div>
                          <div className={styles.arrowIcon}>
                            <ArrowRightIcon />
                          </div>
                        </div>
                      
                    )
                  }
                </div>
            }
          </div>
        </div>

        <div className={styles.rightSidebar}>
          <div className={styles.multipleEntries}>
            <div className={styles.header}>
              <InfoIcon />
              <Delimiter />
              {t('Generate Report')}
            </div>
            <div className={styles.sidebarTitle}>{t('Report period')}</div>
            <DRP
              initRange={dateRange}
              onChange={setDateRange}
              small
              right
            />
            <div className={styles.sidebarContent}>
              <FavoriteFilters
                leftOffset={252}
                fullWidth
                style={{paddingTop: 10}}
                companyId={companyId}
                active={allCustomCategories.length && Object.keys(filteredCustomCategory).length && employees.length && places.length && skills.length && jobTypes.length}
                type="reports"
                onChangeActive={handleChangeActiveFavoriteFilter}
                currentFilters={{
                  start: moment(dateRange.startDate).format('YYYY-MM-DD'),
                  end: moment(dateRange.endDate).format('YYYY-MM-DD'),
                  places: places.filter(p => p.checked).map((place) => place.id),
                  jobTypes: jobTypes.filter(jt => jt.checked).map((spec) => spec.id),
                  employees: getCheckedEmployeeIds(employees),
                  skills: skills.filter(s => s.checked).map((emp) => emp.id),
                  ...(Object.entries(filteredCustomCategory).reduce((acc, [categoryId, items]) => {
                    return {
                      ...acc,
                      [`custom_category_${categoryId}`]: items.filter(item => item.checked).map((item) => item.id),
                    }
                  }, {})),
                  show_costs: costState.show_costs,
                  show_earnings: costState.show_earnings,
                  show_profit: costState.show_profit,
                  show_images: showImages,
                }} />
              {
                permissions.cost && (
                  <StyledCheckbox
                    label={t('Show Costs')}
                    id='show_costs'
                    onChange={costsCheckboxesHandler}
                    checked={costState.show_costs}
                    disabled={costState.show_profit || costState.show_earnings}
                  />
                )
              }
              {
                permissions.earnings && (
                  <StyledCheckbox
                    label={t('Show Earnings')}
                    id='show_earnings'
                    onChange={costsCheckboxesHandler}
                    checked={costState.show_earnings}
                  />
                )
              }
              {
                permissions.profit && (
                  <StyledCheckbox
                    label={t('Show Profit')}
                    id='show_profit'
                    onChange={costsCheckboxesHandler}
                    checked={costState.show_profit}
                  />
                )
              }
              {
                permissions.comments_photo && end_day_photo
                  ? <StyledCheckbox
                      label={t('Show images')}
                      id='show_images'
                      onChange={(_, checked) => setShowImages(checked)}
                      checked={showImages}
                    />
                  : null
              }
              {
                (permissions.places && !permissions.reports_assigned_place) && (
                  <>
                    <div className={styles.sidebarTitle}>{t('Places')}</div>
                    <div className={styles.checkboxGroupWrapper}>
                      <CheckboxGroupV2 items={places ?? []} onChange={(c) => filterChecked(c, 'places')} />
                    </div>
                  </>
                )
              }
              {
                <>
                  <div className={styles.sidebarTitle}>{t('Employees')}</div>
                  <div className={classNames(styles.checkboxGroupWrapper, 'styledDropdown')}>
                    <CheckboxGroupV2
                      items={employees ?? []}
                      onChange={(c) => filterChecked(c, 'employees')}
                    />
                  </div>
                </>
              }
              {
                permissions.jobs && (
                  <>
                    <div className={styles.sidebarTitle}>{t('Job types')}</div>
                    <div className={styles.checkboxGroupWrapper}>
                      <CheckboxGroupV2
                        items={jobTypes ?? []}
                        onChange={(c) => filterChecked(c, 'jobTypes')}
                      />
                    </div>
                  </>
                )
              }
              {
                permissions.custom_category && allCustomCategories?.length && (
                  allCustomCategories.map((category) => (
                    <div key={category.id} className={styles.sidebarBlock}>
                      <div className={styles.sidebarTitle}>
                        {category.name}
                      </div>
                      <div className={styles.checkboxGroupWrapper}>
                        <CheckboxGroupV2 items={filteredCustomCategory?.[category.id] || []} onChange={handleChangeCustomeCategoryFilter(category.id)} />
                      </div>
                    </div>
                  ))
                )
              }
              {
                <>
                  <div className={styles.sidebarTitle}>{t('Skills')}</div>
                  <div className={styles.checkboxGroupWrapper}>
                    <CheckboxGroupV2 items={skills ?? []} onChange={(c) => filterChecked(c, 'skills')} />
                  </div>
                </>
              }
            </div>
            {
              permissions.reports_generate && (
                <div className={styles.actions}>
                  <Button onClick={sendRequest} fillWidth>{t('Generate Report')}</Button>
                </div>
              )
            }
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
